import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Portal from "../../../../../components/portal/Portal";
import TextWrapper from "../../../../../components/textWrapper/TextWrapper";
import { canBePlacedUnder, isSetExecutorRole } from "../../../../../constants/roleRules";
import { showConfirmationModal } from "../../../../../features/confirmationModal/ConfirmationModalSlice";
import styles from "./oneHierarchy.module.css";

const OneHierarchy = ({ item, isMain = 1, index, previousAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChildrenOpen, setIsChildrenOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    left: 0,
    top: 0,
    maxHeight: 220,
  });
  const actionMenuRef = useRef(null);
  const menuRef = useRef(null);
  const closeMenuTimeoutRef = useRef(null);

  const dispatch = useDispatch();
  const { list: admins } = useSelector((state) => state.admins);

  const currentAdmin =
    item?.role && item?.name
      ? item
      : admins.find((admin) => admin._id === item._id);

  const adminAddList = admins.filter(
    (admin) =>
      canBePlacedUnder(currentAdmin?.role?.role, admin?.role?.role) &&
      !item.children.some((child) => child._id === admin._id),
  );

  const clearCloseMenuTimeout = useCallback(() => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!actionMenuRef.current) return;

    const rect = actionMenuRef.current.getBoundingClientRect();
    const spacing = 12;
    const menuWidth = 248;
    const preferredHeight = 220;
    const spaceBelow = window.innerHeight - rect.top - spacing;
    const spaceAbove = rect.bottom - spacing;
    const openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;
    const availableSpace = Math.max(
      openUpward ? spaceAbove : spaceBelow,
      120,
    );
    const maxHeight = Math.min(preferredHeight, availableSpace);

    setMenuPosition({
      left: Math.max(
        spacing,
        Math.min(
          rect.right + spacing,
          window.innerWidth - menuWidth - spacing,
        ),
      ),
      top: openUpward
        ? Math.max(spacing, rect.bottom - maxHeight)
        : Math.max(
            spacing,
            Math.min(rect.top, window.innerHeight - maxHeight - spacing),
          ),
      maxHeight,
    });
  }, []);

  const openMenu = useCallback(() => {
    clearCloseMenuTimeout();

    if (adminAddList.length === 0) {
      setIsOpen(false);
      return;
    }

    updateMenuPosition();
    setIsOpen(true);
  }, [adminAddList.length, clearCloseMenuTimeout, updateMenuPosition]);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      closeMenuTimeoutRef.current = null;
    }, 120);
  }, [clearCloseMenuTimeout]);

  const handleAddAdmin = (addedAdmin) => {
    setIsOpen(false);
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من اضافة الموظف ؟",
        data: {
          adminId: currentAdmin._id,
          newAdminIds: [addedAdmin._id],
        },
        actionType: "addControlledAdmin",
      })
    );
  };

  const handleDeleteAdmin = (removeAdminId) => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من ازالة الموظف ؟",
        data: {
          adminId: previousAdmin._id,
          removeAdminIds: [removeAdminId],
        },
        actionType: "removeControlledAdmin",
      })
    );
  };

  const nextAdmin = item || previousAdmin;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    const handlePointerDown = (event) => {
      const target = event.target;

      if (
        actionMenuRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    return () => {
      clearCloseMenuTimeout();
    };
  }, [clearCloseMenuTimeout]);

  return (
    <div
      className={
        isMain
          ? `${styles.mainContainer} ${styles.imMain} ${
              isOpen ? styles.menuOpen : ""
            }`
          : `${styles.mainContainer} ${isOpen ? styles.menuOpen : ""}`
      }
    >
      <div className={styles.container}>
        <div className={styles.infoWrapper}>
          <TextWrapper textStyle={"textMid"}>
            اسم الموظف : {currentAdmin?.name}
          </TextWrapper>
          <TextWrapper textStyle={"textMid"}>
            الوظيفه : {currentAdmin?.role?.role}
          </TextWrapper>
          {isMain === 0 && (
            <div
              className={styles.removeAdminWrapper}
              onClick={() => handleDeleteAdmin(item._id)}
            >
              <TextWrapper textStyle={"textSmall"}>ازالة الموظف</TextWrapper>
            </div>
          )}
        </div>
        {!isSetExecutorRole(currentAdmin?.role?.role) && (
          <div className={styles.optionsWrapper}>
            <div
              className={styles.actionMenu}
              ref={actionMenuRef}
              onMouseEnter={openMenu}
              onMouseLeave={scheduleCloseMenu}
            >
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => {
                  if (isOpen) {
                    clearCloseMenuTimeout();
                    setIsOpen(false);
                  } else {
                    openMenu();
                  }
                }}
                aria-expanded={isOpen}
              >
                <span className={styles.plusIcon}>+</span>
              </button>
            </div>

            <button
              type="button"
              className={
                isChildrenOpen
                  ? `${styles.iconButton} ${styles.opened}`
                  : styles.iconButton
              }
              onClick={() => setIsChildrenOpen((prev) => !prev)}
            >
              <span className={styles.chevronIcon}>▾</span>
            </button>
          </div>
        )}
      </div>
      {isOpen && adminAddList.length > 0 ? (
        <Portal>
          <div
            ref={menuRef}
            className={styles.adminsWrapper}
            style={{
              left: `${menuPosition.left}px`,
              top: `${menuPosition.top}px`,
              maxHeight: `${menuPosition.maxHeight}px`,
            }}
            onMouseEnter={clearCloseMenuTimeout}
            onMouseLeave={scheduleCloseMenu}
          >
            {adminAddList.map((admin) => {
              return (
                <div
                  key={admin._id}
                  className={styles.oneAdminWrapper}
                  onClick={() => handleAddAdmin(admin)}
                >
                  <TextWrapper textStyle={"textMid"}>
                    اسم الموظف : {admin?.name}
                  </TextWrapper>
                  <TextWrapper textStyle={"textMid"}>
                    الوظيفه : {admin?.role?.role}
                  </TextWrapper>
                </div>
              );
            })}
          </div>
        </Portal>
      ) : null}
      <div className={styles.childrenContainer}>
        {isChildrenOpen &&
          item?.children?.map((child, childIndex) => {
            return (
              <OneHierarchy
                item={child}
                isMain={0}
                key={child._id}
                index={childIndex}
                previousAdmin={nextAdmin}
              />
            );
          })}
      </div>
      {isMain === 0 && (
        <div className={styles.indexContainer}>
          <TextWrapper textStyle={"textSmall"}>{index + 1}</TextWrapper>
        </div>
      )}
    </div>
  );
};

export default OneHierarchy;
